import { useCallback, useEffect, useState } from 'react';
import { authApi, CurrentUser } from '../services/api';
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
  subscribeAuthInvalidated,
} from '../services/authStorage';

export type BindType = 'none' | 'username_password' | 'wechat';

export interface BindState {
  need_bind: boolean;
  bind_type: BindType;
}

export const useAuth = () => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  /** 统一绑定状态：是否需要补充绑定以及绑定类型 */
  const [bindState, setBindState] = useState<BindState>({ need_bind: false, bind_type: 'none' });

  useEffect(() => {
    let isActive = true;
    let isUnmounting = false;

    const clearUser = () => {
      if (isActive) {
        setUser(null);
        setIsLoading(false);
      }
    };
    const unsubscribe = subscribeAuthInvalidated(clearUser);

    if (!getAccessToken()) {
      clearUser();
      return () => {
        isActive = false;
        isUnmounting = true;
        unsubscribe();
      };
    }

    authApi.getCurrentUser()
      .then((response) => {
        if (isActive) {
          setUser(response.data.data);
        }
      })
      .catch((error) => {
        // 如果是组件卸载导致的错误，不清除token
        if (isUnmounting) {
          return;
        }
        // 如果是网络错误（如ERR_ABORTED），也不清除token
        if (error?.code === 'ERR_ABORTED' || error?.name === 'AbortError') {
          if (isActive) {
            setIsLoading(false);
          }
          return;
        }
        // 只有真正的认证错误（如401）才清除token
        if (error?.response?.status === 401) {
          clearAccessToken();
          clearUser();
        } else {
          // 其他网络错误，保持token但结束loading
          if (isActive) {
            setIsLoading(false);
          }
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
      isUnmounting = true;
      unsubscribe();
    };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    setAccessToken(response.data.data.access_token);

    // 优先用登录接口返回的绑定状态（同步获取，无需额外请求）
    const loginData = response.data.data;
    if (loginData.need_bind !== undefined && loginData.bind_type) {
      setBindState({
        need_bind: loginData.need_bind,
        bind_type: loginData.bind_type,
      });
    }

    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser.data.data);
      // 用 /me 返回的绑定状态覆盖（更权威）
      if (currentUser.data.data.need_bind !== undefined && currentUser.data.data.bind_type) {
        setBindState({
          need_bind: currentUser.data.data.need_bind,
          bind_type: currentUser.data.data.bind_type,
        });
      }
    } catch (error) {
      clearAccessToken();
      throw error;
    }
  }, []);

  /** 微信扫码登录（zbapi JWT 模式）。
   *
   * zbapi 平台已升级，扫码后直接返回 HS256 JWT（payload 含 sub=member_id）。
   * 前端轮询 zbapi 拿到 JWT 后，调用本方法用 zbapi JWT 换取 aibiaoshu JWT。
   */
  const wechatLoginByJwt = useCallback(async (zbapi_jwt: string) => {
    const response = await authApi.wechatLoginByJwt(zbapi_jwt);
    const loginData = response.data.data;
    setAccessToken(loginData.access_token);

    // 优先用新字段 need_bind + bind_type，fallback 到旧字段 need_bind_info
    if (loginData.need_bind !== undefined && loginData.bind_type) {
      setBindState({
        need_bind: loginData.need_bind,
        bind_type: loginData.bind_type,
      });
    } else if (loginData.need_bind_info !== undefined) {
      // 兼容旧后端（只有 need_bind_info）
      setBindState({
        need_bind: !!loginData.need_bind_info,
        bind_type: loginData.need_bind_info ? 'username_password' : 'none',
      });
    }

    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser.data.data);
      // 用 /me 返回的绑定状态覆盖（更权威）
      if (currentUser.data.data.need_bind !== undefined && currentUser.data.data.bind_type) {
        setBindState({
          need_bind: currentUser.data.data.need_bind,
          bind_type: currentUser.data.data.bind_type,
        });
      }
    } catch (error) {
      clearAccessToken();
      setBindState({ need_bind: false, bind_type: 'none' });
      throw error;
    }
  }, []);

  /** 微信新用户绑定用户名密码 */
  const bindInfo = useCallback(async (username: string, password: string) => {
    await authApi.bindInfo(username, password);
    // 刷新用户信息，让 username 同步到 user 对象，并获取最新绑定状态
    const currentUser = await authApi.getCurrentUser();
    setUser(currentUser.data.data);
    const meData = currentUser.data.data;
    if (meData.need_bind !== undefined && meData.bind_type) {
      setBindState({
        need_bind: meData.need_bind,
        bind_type: meData.bind_type,
      });
    } else {
      // /me 未返回绑定状态（旧后端），假设绑定成功后只需检查 openid
      setBindState({
        need_bind: !meData.id || false,
        bind_type: 'none',
      });
    }
  }, []);

  /** 普通账号绑定微信（zbapi JWT 模式） */
  const bindWechat = useCallback(async (zbapi_jwt: string) => {
    const response = await authApi.bindWechat(zbapi_jwt);
    const resultData = response.data.data;

    // 用接口返回的绑定状态更新（更权威，无需再调 /me）
    if (resultData.need_bind !== undefined && resultData.bind_type) {
      setBindState({
        need_bind: resultData.need_bind,
        bind_type: resultData.bind_type,
      });
    } else {
      // fallback：再调 /me 获取
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser.data.data);
      const meData = currentUser.data.data;
      if (meData.need_bind !== undefined && meData.bind_type) {
        setBindState({
          need_bind: meData.need_bind,
          bind_type: meData.bind_type,
        });
      }
    }
  }, []);

  const logout = useCallback(() => {
    clearAccessToken();
    setUser(null);
    setBindState({ need_bind: false, bind_type: 'none' });
  }, []);

  return {
    user,
    isLoading,
    login,
    wechatLoginByJwt,
    bindInfo,
    bindWechat,
    bindState,
    setBindState,
    logout,
  };
};
